import React, { useMemo, useRef, useState } from "react";

function toNumber(value, fallback) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getPercent(value, min, max) {
  if (max <= min) {
    return 0;
  }
  return ((value - min) / (max - min)) * 100;
}

export default function MetricRangeSlider({
  minValue,
  maxValue,
  rangeMin,
  rangeMax,
  step = 1,
  onMinChange,
  onMaxChange,
  formatValue = (value) => String(value),
}) {
  const [activeThumb, setActiveThumb] = useState(null);
  const controlRef = useRef(null);
  const activeDragRef = useRef(null);

  const bounds = useMemo(() => {
    const minBound = toNumber(rangeMin, 0);
    const maxBound = Math.max(toNumber(rangeMax, minBound), minBound);
    const selectedMin = clamp(toNumber(minValue, minBound), minBound, maxBound);
    const selectedMax = clamp(toNumber(maxValue, maxBound), minBound, maxBound);
    return {
      minBound,
      maxBound,
      selectedMin: Math.min(selectedMin, selectedMax),
      selectedMax: Math.max(selectedMin, selectedMax),
    };
  }, [maxValue, minValue, rangeMax, rangeMin]);

  const minPercent = getPercent(bounds.selectedMin, bounds.minBound, bounds.maxBound);
  const maxPercent = getPercent(bounds.selectedMax, bounds.minBound, bounds.maxBound);
  const disabled = bounds.maxBound <= bounds.minBound;
  const bubbleValue = activeThumb === "min" ? bounds.selectedMin : bounds.selectedMax;
  const bubblePercent = activeThumb === "min" ? minPercent : maxPercent;

  function handleMinChange(value) {
    const next = clamp(Number(value), bounds.minBound, bounds.selectedMax);
    onMinChange(String(Math.round(next)));
  }

  function handleMaxChange(value) {
    const next = clamp(Number(value), bounds.selectedMin, bounds.maxBound);
    onMaxChange(String(Math.round(next)));
  }

  function valueFromPointer(clientX) {
    const rect = controlRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) {
      return bounds.minBound;
    }
    const percent = clamp((clientX - rect.left) / rect.width, 0, 1);
    const rawValue = bounds.minBound + percent * (bounds.maxBound - bounds.minBound);
    const stepped = Math.round(rawValue / step) * step;
    return clamp(stepped, bounds.minBound, bounds.maxBound);
  }

  function updateThumbFromPointer(thumb, clientX) {
    const next = valueFromPointer(clientX);
    if (thumb === "min") {
      handleMinChange(next);
    } else {
      handleMaxChange(next);
    }
  }

  function handleThumbPointerDown(event, thumb) {
    if (disabled) {
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    activeDragRef.current = thumb;
    setActiveThumb(thumb);
    updateThumbFromPointer(thumb, event.clientX);
  }

  function handleThumbPointerMove(event, thumb) {
    if (activeDragRef.current !== thumb) {
      return;
    }
    updateThumbFromPointer(thumb, event.clientX);
  }

  function handleTrackPointerDown(event) {
    if (disabled || event.target?.closest?.(".metric-range-thumb")) {
      return;
    }
    const next = valueFromPointer(event.clientX);
    const distanceToMin = Math.abs(next - bounds.selectedMin);
    const distanceToMax = Math.abs(next - bounds.selectedMax);
    const thumb = distanceToMin <= distanceToMax ? "min" : "max";
    setActiveThumb(thumb);
    if (thumb === "min") {
      handleMinChange(next);
    } else {
      handleMaxChange(next);
    }
  }

  function stopDragging() {
    activeDragRef.current = null;
    setActiveThumb(null);
  }

  function handleKeyDown(event, thumb) {
    if (disabled) {
      return;
    }
    const delta = event.key === "ArrowLeft" || event.key === "ArrowDown"
      ? -step
      : event.key === "ArrowRight" || event.key === "ArrowUp"
      ? step
      : event.key === "Home"
      ? bounds.minBound - (thumb === "min" ? bounds.selectedMin : bounds.selectedMax)
      : event.key === "End"
      ? bounds.maxBound - (thumb === "min" ? bounds.selectedMin : bounds.selectedMax)
      : 0;

    if (!delta) {
      return;
    }
    event.preventDefault();
    setActiveThumb(thumb);
    if (thumb === "min") {
      handleMinChange(bounds.selectedMin + delta);
    } else {
      handleMaxChange(bounds.selectedMax + delta);
    }
  }

  return (
    <div
      className={`metric-range${disabled ? " disabled" : ""}`}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
    >
      <div className="metric-range-values" aria-hidden="true">
        <span>{formatValue(bounds.selectedMin)}</span>
        <span>{formatValue(bounds.selectedMax)}</span>
      </div>

      <div
        ref={controlRef}
        className="metric-range-control"
        style={{
          "--range-min": `${minPercent}%`,
          "--range-max": `${maxPercent}%`,
          "--bubble-left": `${bubblePercent}%`,
        }}
        onPointerDown={handleTrackPointerDown}
      >
        {activeThumb ? (
          <div className={`metric-range-bubble ${activeThumb}`}>
            {formatValue(bubbleValue)}
          </div>
        ) : null}
        <div className="metric-range-track" />
        <button
          type="button"
          className="metric-range-thumb min"
          style={{ left: `${minPercent}%` }}
          disabled={disabled}
          role="slider"
          aria-label="Minimum value"
          aria-valuemin={bounds.minBound}
          aria-valuemax={bounds.selectedMax}
          aria-valuenow={bounds.selectedMin}
          onFocus={() => setActiveThumb("min")}
          onBlur={() => setActiveThumb(null)}
          onKeyDown={(event) => handleKeyDown(event, "min")}
          onPointerDown={(event) => handleThumbPointerDown(event, "min")}
          onPointerMove={(event) => handleThumbPointerMove(event, "min")}
          onPointerUp={stopDragging}
          onPointerCancel={stopDragging}
        />
        <button
          type="button"
          className="metric-range-thumb max"
          style={{ left: `${maxPercent}%` }}
          disabled={disabled}
          role="slider"
          aria-label="Maximum value"
          aria-valuemin={bounds.selectedMin}
          aria-valuemax={bounds.maxBound}
          aria-valuenow={bounds.selectedMax}
          onFocus={() => setActiveThumb("max")}
          onBlur={() => setActiveThumb(null)}
          onKeyDown={(event) => handleKeyDown(event, "max")}
          onPointerDown={(event) => handleThumbPointerDown(event, "max")}
          onPointerMove={(event) => handleThumbPointerMove(event, "max")}
          onPointerUp={stopDragging}
          onPointerCancel={stopDragging}
        />
      </div>
    </div>
  );
}
